import React, {
    FC, KeyboardEvent, useEffect, useState,
} from 'react';

import { Dropdown } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { FitMode } from '../types';
import {
    CaretIcon,
    FitPageIcon,
    FitWidthIcon,
    GridIcon,
    HandIcon,
    SearchIcon,
    ZoomInIcon,
    ZoomOutIcon,
} from './Icons';
import styles from '../PdfTileViewer.module.scss';

export type SidebarView = 'thumbnails' | 'outline';

interface ToolbarProps {
    current: number;
    total: number;
    fitMode: FitMode | null;
    isPanActive: boolean;
    hasPan: boolean;
    isSearchOpen: boolean;
    carouselMode: boolean;
    onToggleSidebar: () => void;
    onSidebarView: (view: SidebarView) => void;
    onGoTo: (index: number) => void;
    onTogglePan: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onToggleFit: () => void;
    onToggleSearch: () => void;
}

const Separator: FC<{ className?: string }> = ({ className }) => (
    <div className={classNames(styles.separator, className)} />
);

const Toolbar: FC<ToolbarProps> = ({
    current,
    total,
    fitMode,
    isPanActive,
    hasPan,
    isSearchOpen,
    carouselMode,
    onToggleSidebar,
    onSidebarView,
    onGoTo,
    onTogglePan,
    onZoomIn,
    onZoomOut,
    onToggleFit,
    onToggleSearch,
}) => {
    const { t } = useTranslation();
    const [draft, setDraft] = useState<string>(String(current + 1));

    useEffect(() => setDraft(String(current + 1)), [current]);

    const commitDraft = (): void => {
        const value = Number(draft);
        if (Number.isFinite(value) && draft !== '') {
            onGoTo(value - 1);
        } else {
            setDraft(String(current + 1));
        }
    };

    const onPageKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            commitDraft();
        }
    };

    const fitTitle = fitMode === 'page' ? t('viewer.tileViewer.fitToWidth') : t('viewer.tileViewer.fitToPage');

    const menuItems = [
        { key: 'thumbnails', label: t('viewer.tileViewer.thumbnails') },
        { key: 'outline', label: t('viewer.tileViewer.outline') },
    ];

    return (
        <div className={classNames(styles.toolbar, { [styles.carousel]: carouselMode })}>
            <div className={styles['sidebar-group']}>
                <button
                    type="button"
                    className={styles.tool}
                    title={t('viewer.tileViewer.toggleSidebar')}
                    aria-label={t('viewer.tileViewer.toggleSidebar')}
                    onClick={onToggleSidebar}
                >
                    <GridIcon />
                </button>
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: menuItems,
                        onClick: ({ key }) => onSidebarView(key as SidebarView),
                    }}
                >
                    <button
                        type="button"
                        className={styles.tool}
                        title={t('viewer.tileViewer.sidebarViews')}
                        aria-label={t('viewer.tileViewer.sidebarViews')}
                    >
                        <CaretIcon className={styles.caret} />
                    </button>
                </Dropdown>
            </div>
            <div className={styles.group}>
                <span className={styles['page-label']}>{t('viewer.tileViewer.page')}</span>
                <div className={styles.pager}>
                    <button
                        type="button"
                        disabled={current <= 0}
                        title={t('viewer.tileViewer.previousPage')}
                        aria-label={t('viewer.tileViewer.previousPage')}
                        onClick={() => onGoTo(current - 1)}
                    >
                        &#9664;
                    </button>
                    <input
                        type="number"
                        min={1}
                        max={total}
                        value={draft}
                        aria-label={t('viewer.tileViewer.page')}
                        onChange={(event) => setDraft(event.target.value)}
                        onBlur={commitDraft}
                        onKeyDown={onPageKeyDown}
                    />
                    <button
                        type="button"
                        disabled={current >= total - 1}
                        title={t('viewer.tileViewer.nextPage')}
                        aria-label={t('viewer.tileViewer.nextPage')}
                        onClick={() => onGoTo(current + 1)}
                    >
                        &#9654;
                    </button>
                </div>
                <span className={styles['of-pages']}>{t('viewer.tileViewer.ofPages', { total })}</span>
                <span className={styles['of-pages-compact']}>{`/ ${total}`}</span>
            </div>
            <Separator />
            {hasPan && (
                <>
                    <button
                        type="button"
                        className={classNames(styles.tool, { [styles.on]: isPanActive })}
                        title={t('viewer.tileViewer.pan')}
                        aria-label={t('viewer.tileViewer.pan')}
                        onClick={onTogglePan}
                    >
                        <HandIcon />
                    </button>
                    <Separator />
                </>
            )}
            <button
                type="button"
                className={classNames(styles.tool, styles['zoom-tool'])}
                title={t('viewer.tileViewer.zoomOut')}
                aria-label={t('viewer.tileViewer.zoomOut')}
                onClick={onZoomOut}
            >
                <ZoomOutIcon />
            </button>
            <Separator className={styles['zoom-tool']} />
            <button
                type="button"
                className={classNames(styles.tool, styles['zoom-tool'])}
                title={t('viewer.tileViewer.zoomIn')}
                aria-label={t('viewer.tileViewer.zoomIn')}
                onClick={onZoomIn}
            >
                <ZoomInIcon />
            </button>
            <Separator className={styles['zoom-tool']} />
            <button
                type="button"
                className={classNames(styles.tool, { [styles.on]: !!fitMode })}
                title={fitTitle}
                aria-label={fitTitle}
                onClick={onToggleFit}
            >
                {fitMode === 'width' ? <FitWidthIcon /> : <FitPageIcon />}
            </button>
            <Separator />
            <button
                type="button"
                className={classNames(styles.tool, { [styles.on]: isSearchOpen })}
                title={t('viewer.tileViewer.search')}
                aria-label={t('viewer.tileViewer.search')}
                onClick={onToggleSearch}
            >
                <SearchIcon />
            </button>
            <Separator />
        </div>
    );
};

export default Toolbar;
